import React, { useState, useEffect, FC, ReactElement, ReactNode } from "react";
import { MenuItem, Select, FormControl, InputLabel, FormHelperText, SelectChangeEvent, Skeleton } from "@mui/material";
import { useSettingsContext } from "../../../Settings/hooks/useSettingsContext";
import { useTranslation } from "react-i18next";
import { CategoryItem, CategorySelectFieldParams } from "../types/CRMCategorySelectField";
import { default as categorySelectService } from "../services/categorySelect";

const CategorySelectField: FC<CategorySelectFieldParams> = ({
  onChange,
  value,
  label,
  readOnly,
  onAvailableItemsUpdate,
  onSelectUpdate,
  meta,
  autolectures,
  unsigned=false,
}) => {

  const Settings = useSettingsContext();
  const { t } = useTranslation();

  const [ state, setState ] = useState<"fetching" | "success" | "error">("fetching");
  const [availableItems, setAvailableItems] = useState<CategoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);

  const handleChange = (
    event: SelectChangeEvent<unknown>,
    child: ReactNode
  ) => {
    const value = Number(event.target.value);

    setSelectedItem(value);
    onChange(value);
    if (typeof onSelectUpdate === "function" && React.isValidElement(child)) {
      onSelectUpdate(value, child.props.children);
    }
  };

  const setAutolectures = () => {
    let autoreads = Settings?.features?.contactAutoReads ?? false;
    if (autoreads){
      if (autolectures) {
        const categ = availableItems.filter((ct) => ct.id === 20)[0]; //20 is the code for autolectures @lumina
        setSelectedItem(categ.id)
        onChange(categ.id)
      }
    }
  }

  useEffect(() => {
    setState("fetching");

    categorySelectService.getCategories(unsigned)
    .then(response => {
      const items = response.data?.items
      if (!items) return;

      setState("success");

      if (items && typeof onAvailableItemsUpdate === "function") {
        onAvailableItemsUpdate();
      }
      
      let defaultSelectedItem = items?.[0]?.id ?? null;
      if (value) {
        defaultSelectedItem = value;
      }

      setAvailableItems(items)
      setSelectedItem(defaultSelectedItem)
      onChange(defaultSelectedItem);
    })
  }, []);

  useEffect(() => {
    setAutolectures();
    // eslint-disable-next-line
  }, [autolectures, availableItems]);

  return (
    availableItems?.length !== 1 ?
      <div>
        {state === "fetching" ? (
          <Skeleton data-testid="skeleton-categories" height={56} />
        ) : (
        <>
          <FormControl fullWidth={true}
            disabled={readOnly}
          >
            <InputLabel shrink htmlFor="" id="category-name-label">
              {(label || t('common:text.contact_category')) + "*"}
            </InputLabel>
            <Select<number>
              value={selectedItem ?? ''}
              onChange={handleChange}
              autoWidth={false}
              labelId="category-name-label"
              id="category-name"
            >
              {availableItems.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {!!(meta?.touched) &&
            <FormHelperText error={true}>
              {meta?.error}
            </FormHelperText>
          }
        </>
        )
      }
      </div>
      : 
      <div></div>
  );
};

export default CategorySelectField;
